// スタジオのタスクと自動化（docs/studio-automation.md）。作問から公開までを16のタスクに分け、
// それぞれ「自動／自動＋確認／手動」を切り替える。サーバーと画面の両方でこの定義を使う。
import { z } from 'zod';
import { PACK_MIN_QUESTIONS } from './editions';
import { TOURNAMENT_LIMITS_DEFAULT } from './limits';
import { RulesSchema, presetRules } from './rules';
import { packGenresSchema } from './production';

export const TASK_MODES = ['auto', 'auto_confirm', 'manual'] as const;
export type TaskMode = (typeof TASK_MODES)[number];
export const TASK_MODE_LABEL: Record<TaskMode, string> = { auto: '自動', auto_confirm: '自動＋確認', manual: '手動' };

/** 制作エージェントの役（どの AI が担当するかは設定で入れ替える） */
export const AGENT_ROLES = ['author', 'factcheck', 'review'] as const;
export type AgentRole = (typeof AGENT_ROLES)[number];
export const AGENT_ROLE_LABEL: Record<AgentRole, string> = { author: '作問', factcheck: 'ファクトチェック', review: '品質審査' };

export const AI_IDS = ['claude', 'gemini', 'codex'] as const;
export type AiId = (typeof AI_IDS)[number];
export const AI_LABEL: Record<AiId, string> = { claude: 'Claude Code', gemini: 'Gemini', codex: 'Codex' };

export const TASK_IDS = ['T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10', 'T11', 'T12', 'T13', 'T14', 'T15', 'T16'] as const;
export type TaskId = (typeof TASK_IDS)[number];

export type TaskDef = {
  id: TaskId;
  label: string;
  /** 初期値 */
  defaultMode: TaskMode;
  /** 選べるモード（T4 は自動で固定、T8 は手動で固定） */
  modes: readonly TaskMode[];
  /** 制作エージェントのどの役で動くか（null はサーバーかプログラム） */
  role: AgentRole | null;
  /** 手動のとき人がすること（設定の画面に出す） */
  manualNote: string;
  /** 使わなくなったタスク（画面に出さない。2026-10-09 T2 作問の計画・T9 差し戻しの対応） */
  retired?: true;
};

const ALL = TASK_MODES;
/** 16のタスク（studio-automation.md 2章） */
export const TASKS: readonly TaskDef[] = [
  // 範囲表は画面から外した（2026-10-09）ので、手動にはできない（案の確認はやることで）
  { id: 'T1', label: '範囲表を作る', defaultMode: 'auto_confirm', modes: ['auto_confirm', 'auto'], role: 'author', manualNote: '—', retired: true },
  { id: 'T2', label: '作問の計画', defaultMode: 'auto', modes: ALL, role: null, manualNote: '作問の指示フォームで作る', retired: true },
  { id: 'T3', label: '作問', defaultMode: 'auto', modes: ALL, role: 'author', manualNote: '指示書をコピーして AI に貼り、JSON を取り込む' },
  { id: 'T4', label: '形式・重複・類似のチェック', defaultMode: 'auto', modes: ['auto'], role: null, manualNote: '—' },
  { id: 'T5', label: 'ファクトチェック', defaultMode: 'auto', modes: ALL, role: 'factcheck', manualNote: '指示書をコピーして AI に貼り、結果を取り込む' },
  { id: 'T6', label: '品質審査', defaultMode: 'auto', modes: ALL, role: 'review', manualNote: '指示書をコピーして AI に貼り、結果を取り込む' },
  { id: 'T7', label: '不合格の書き直し', defaultMode: 'auto', modes: ALL, role: 'author', manualNote: '問題を直すか、捨てる' },
  { id: 'T8', label: '問題の確認（合格・不合格）', defaultMode: 'manual', modes: ['manual'], role: null, manualNote: '確認の画面で合格・直して合格・不合格' },
  { id: 'T9', label: '差し戻しの対応', defaultMode: 'manual', modes: ALL, role: 'author', manualNote: '—', retired: true },
  { id: 'T10', label: '音声を作る', defaultMode: 'auto', modes: ALL, role: null, manualNote: '「音声を作る」を押す' },
  { id: 'T11', label: '音声の確認（読み違いは自動で作り直し、人が聴いて決める）', defaultMode: 'auto', modes: ALL, role: null, manualNote: '聴いて合格' },
  { id: 'T12', label: '収録と配分', defaultMode: 'auto', modes: ALL, role: null, manualNote: '収録する問題を自分で選ぶ' },
  { id: 'T13', label: '説明文・見本の問題', defaultMode: 'auto_confirm', modes: ALL, role: 'author', manualNote: '自分で書く・選ぶ' },
  { id: 'T14', label: '公開', defaultMode: 'manual', modes: ['manual', 'auto'], role: null, manualNote: '公開ボタンを押す' },
  { id: 'T15', label: '定期の再チェック', defaultMode: 'auto', modes: ALL, role: 'factcheck', manualNote: '期限が来た問題を自分で確かめる' },
  { id: 'T16', label: '週次の改善', defaultMode: 'auto_confirm', modes: ALL, role: 'author', manualNote: 'Claude Code の対話で /qs-improve' },
];
/** 画面に出すタスク（使わなくなったものを除く） */
export const ACTIVE_TASKS = TASKS.filter((t) => !t.retired);
export const TASK_BY_ID = Object.fromEntries(TASKS.map((t) => [t.id, t])) as Record<TaskId, TaskDef>;
export const DEFAULT_TASK_MODES = Object.fromEntries(TASKS.map((t) => [t.id, t.defaultMode])) as Record<TaskId, TaskMode>;

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, '時刻は 01:00 の形で入れてください');
const taskModes = z.partialRecord(z.enum(TASK_IDS), z.enum(TASK_MODES)).superRefine((m, c) => {
  for (const [id, mode] of Object.entries(m)) {
    const def = TASK_BY_ID[id as TaskId];
    if (mode && !def.modes.includes(mode)) {
      c.addIssue({ code: 'custom', path: [id], message: `「${def.label}」は${def.modes.map((x) => TASK_MODE_LABEL[x]).join('か')}にしかできません` });
    }
  }
});

const limitsFields = {
  /** 承認待ちの上限。これに達すると作問を止める */
  stampQueueMax: z.number().int().min(1).max(5000),
  /** 音声の月の上限（ドル） */
  audioMonthlyUsd: z.number().min(0).max(1000),
  /** AI ごとに同時に進める仕事の数（2026-10-09）。混んでいるときは列に並び、空いたものから順に渡す */
  parallelPerAi: z.number().int().min(1).max(10),
  /** 公開に要る最低の問題数（2026-10-09。無料・有料） */
  minQuestionsFree: z.number().int().min(1).max(1000),
  minQuestionsPaid: z.number().int().min(1).max(1000),
};
export const DEFAULT_LIMITS = { stampQueueMax: 200, audioMonthlyUsd: 8, parallelPerAi: 3, minQuestionsFree: PACK_MIN_QUESTIONS.free, minQuestionsPaid: PACK_MIN_QUESTIONS.paid };
const limitsSchema = z.object({
  stampQueueMax: limitsFields.stampQueueMax.default(DEFAULT_LIMITS.stampQueueMax),
  audioMonthlyUsd: limitsFields.audioMonthlyUsd.default(DEFAULT_LIMITS.audioMonthlyUsd),
  parallelPerAi: limitsFields.parallelPerAi.default(DEFAULT_LIMITS.parallelPerAi),
  minQuestionsFree: limitsFields.minQuestionsFree.default(DEFAULT_LIMITS.minQuestionsFree),
  minQuestionsPaid: limitsFields.minQuestionsPaid.default(DEFAULT_LIMITS.minQuestionsPaid),
});

/**
 * 外部 API の送る速さの上限（2026-10-12）。モデルごとに 1分あたり（rpm）と1日あたり（rpd。null は数えない）。
 * Google の上限（AI Studio の表示）より少し低くして、429 を受けないようにする。Gemini 3.8 Flash TTS は Google が 1分10回・1日100回
 */
/** reset：1日の回数を数え直す時刻（utc＝日本時間の朝9時、jst＝日本時間の0時。2026-10-13） */
export const ApiRateSchema = z.object({ rpm: z.number().int().min(1).max(1000), rpd: z.number().int().min(1).max(1_000_000).nullable(), reset: z.enum(['utc', 'jst']).default('utc') });
export type ApiRate = z.infer<typeof ApiRateSchema>;
export const DEFAULT_API_RATE: Record<string, ApiRate> = {
  'gemini-3.8-flash-tts': { rpm: 8, rpd: 100, reset: 'utc' },
  'gemini-3.5-flash-lite': { rpm: 8, rpd: null, reset: 'utc' },
  /** Vertex AI（急ぎの窓口・人が押したときだけ）：1分20回・1日800回、日本時間0時に戻す（オーナーの指定。2026-10-13） */
  'vertex:gemini-3.8-flash-tts': { rpm: 20, rpd: 800, reset: 'jst' },
};
/** モデルの上限（設定に無ければ初期値。どちらにも無ければ制限しない） */
export const apiRateOf = (settings: { apiRate?: Record<string, ApiRate> }, model: string): ApiRate | null => settings.apiRate?.[model] ?? DEFAULT_API_RATE[model] ?? null;

/** 主催者が大会を作るときの初期値（2026-10-09。スタジオのシステム設定 › 大会の初期値） */
export const TournamentDefaultsSchema = z.object({
  /** ルールの初期値（プリセットと詳細） */
  rules: RulesSchema.default(presetRules('easy')),
  /** 出題順の初期値：pack＝選んだ順、random＝選んだ中からランダム */
  order: z.enum(['pack', 'random']).default('pack'),
  /** 個人戦の人数の上限 */
  maxPlayers: z.number().int().min(2).max(50).default(TOURNAMENT_LIMITS_DEFAULT.maxPlayers),
  /** チーム戦のチームの数の上限 */
  maxTeams: z.number().int().min(2).max(8).default(TOURNAMENT_LIMITS_DEFAULT.maxTeams),
  /** チーム戦の、全チーム合わせた人数の上限（チームごとの人数は自由） */
  maxTeamPlayers: z.number().int().min(2).max(80).default(TOURNAMENT_LIMITS_DEFAULT.maxTeamPlayers),
});
export type TournamentDefaults = z.infer<typeof TournamentDefaultsSchema>;
/** 主催者の画面に出す人数の上限 */
export type TournamentLimits = Pick<TournamentDefaults, 'maxPlayers' | 'maxTeams' | 'maxTeamPlayers'>;

/** スタジオの設定（設定の画面）。全体の初期値。パックごとに taskModes を上書きできる */
export const StudioSettingsSchema = z.object({
  taskModes: taskModes.default({}),
  /** どの役をどの AI が担当するか */
  aiRoles: z.object({ author: z.enum(AI_IDS), factcheck: z.enum(AI_IDS), review: z.enum(AI_IDS) }).default({ author: 'gemini', factcheck: 'gemini', review: 'gemini' }),
  /** 制作エージェントが動く時間帯（日本時間）。2026-10-09 から使わない（いつでもすぐ動く）。古い設定を読めるよう残す */
  agentWindow: z.object({ start: hhmm, end: hhmm }).default({ start: '01:00', end: '07:00' }),
  /** 「今すぐ動かす」：この時刻までは時間帯の外でも動く */
  runNowUntil: z.iso.datetime().nullable().default(null),
  limits: limitsSchema.default(DEFAULT_LIMITS),
  notify: z.object({ morningMail: z.boolean().default(true) }).default({ morningMail: true }),
  tournament: TournamentDefaultsSchema.default(TournamentDefaultsSchema.parse({})),
  /** 外部 API の送る速さの上限（モデルごと。2026-10-12） */
  apiRate: z.record(z.string().min(1).max(80), ApiRateSchema).default(DEFAULT_API_RATE),
});
export type StudioSettings = z.infer<typeof StudioSettingsSchema>;
/** 変える項目だけ（limits も項目ごとに。渡さない項目は今の値のまま） */
export const StudioSettingsPatch = StudioSettingsSchema.partial().extend({ limits: z.object(limitsFields).partial().optional() });
/** 公開に要る最低の問題数（設定） */
export const minQuestionsOf = (kind: 'free' | 'paid', settings: Pick<StudioSettings, 'limits'>) => (kind === 'paid' ? settings.limits.minQuestionsPaid : settings.limits.minQuestionsFree);
export const DEFAULT_STUDIO_SETTINGS: StudioSettings = StudioSettingsSchema.parse({});

/** パックの自動化の設定（パックの画面） */
export const PackAutomationPatch = z.object({
  paused: z.boolean().optional(),
  /** パックだけモードを上書き（null で全体の初期値に戻す） */
  taskModes: taskModes.optional(),
  /** 予約公開の日時（公開 T14 が自動のときだけ動く。null で取り消し） */
  scheduledAt: z.iso.datetime({ offset: true }).nullable().optional(),
});

/** 「新しいパック」（studio-automation.md 3章）：入れるのは5つだけ */
export const NewPackBody = z.object({
  name: z.string().trim().min(1, '名前を入れてください').max(60),
  /** 詳細・キーワード（単語を並べるだけでもよい） */
  theme: z.string().trim().max(500).default(''),
  audience: z.string().trim().max(40).default(''),
  genres: packGenresSchema.default([]),
  kind: z.enum(['paid', 'free']).default('paid'),
});

/** 範囲表の必要数の合計（割合の目安。パックの問題数ではない） */
export const SCOPE_PLAN_TOTAL = 100;

/** 作問の仕事1件あたりの問題数（1回の呼び出しで5問まとめて作らせる。quiz-production.md 3章） */
export const AUTHOR_CHUNK = 5;

/** モードを決める：パックの上書き → 全体の初期値 → タスクの初期値。固定のタスクは常に固定 */
export function modeOf(task: TaskId, settings: Pick<StudioSettings, 'taskModes'>, packModes?: Partial<Record<TaskId, TaskMode>> | null): TaskMode {
  const def = TASK_BY_ID[task];
  const m = packModes?.[task] ?? settings.taskModes[task] ?? def.defaultMode;
  return def.modes.includes(m) ? m : def.defaultMode;
}

/** 日本時間の時刻（"HH:MM"）が時間帯の中か */
export function inWindow(now: Date, w: { start: string; end: string }) {
  const jst = new Date(now.getTime() + 9 * 3600_000);
  const m = jst.getUTCHours() * 60 + jst.getUTCMinutes();
  const toMin = (s: string) => Number(s.slice(0, 2)) * 60 + Number(s.slice(3, 5));
  const a = toMin(w.start);
  const b = toMin(w.end);
  if (a === b) return true;
  return a < b ? m >= a && m < b : m >= a || m < b;
}

/** 画面の時刻の表記（日本時間 HH:MM） */
export function jstHm(d: Date) {
  const j = new Date(d.getTime() + 9 * 3600_000);
  return `${String(j.getUTCHours()).padStart(2, '0')}:${String(j.getUTCMinutes()).padStart(2, '0')}`;
}
